import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";

const SVC = `${baseUrl}/job-posting`;

const ERROR_MESSAGES = {
  missing_token: "Invalid link.",
  invalid_token: "This interview link is not valid.",
  invite_canceled: "This interview was canceled. Please contact the recruiter.",
  invite_expired: "This interview link has expired.",
  invite_not_yet_valid: "Your interview window hasn't opened yet.",
  already_accepted: "You already accepted. Use the join link in your email.",
  already_rejected:
    "You previously declined. Contact the recruiter to reschedule.",
};

function errorMessage(code) {
  return (
    ERROR_MESSAGES[code] ||
    "Something went wrong. Please try again, or contact the recruiter if the problem persists."
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-6">
      <div className="w-8 h-8 border-2 border-vf-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-4">
      <div className="card max-w-xl w-full p-8 text-center space-y-4">
        {children}
      </div>
    </div>
  );
}

function ErrorScreen({ code }) {
  return (
    <CenterCard>
      <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto text-xl font-bold">
        !
      </div>
      <h2 className="font-semibold text-lg text-vf-base">
        We couldn't load this interview
      </h2>
      <p className="text-sm text-vf-muted">{errorMessage(code)}</p>
    </CenterCard>
  );
}

function AlreadyDecidedScreen({ meta }) {
  if (meta.currentStatus === "ACCEPTED") {
    return (
      <CenterCard>
        <div className="text-5xl">✓</div>
        <h2 className="text-xl font-semibold text-vf-base">
          You've already accepted.
        </h2>
        <p className="text-sm text-vf-muted">
          We've emailed you the interview join link. Check your inbox at{" "}
          <b>{meta.candidateEmail}</b>.
        </p>
        {meta.joinUrl && (
          <a
            href={meta.joinUrl}
            className="btn-primary inline-block mt-2"
            rel="noopener noreferrer"
          >
            Open join link
          </a>
        )}
      </CenterCard>
    );
  }
  if (meta.currentStatus === "REJECTED") {
    return (
      <CenterCard>
        <div className="text-5xl">·</div>
        <h2 className="text-xl font-semibold text-vf-base">
          You previously declined.
        </h2>
        <p className="text-sm text-vf-muted">
          If you'd like to reschedule, please contact the recruiter.
        </p>
      </CenterCard>
    );
  }
  return (
    <CenterCard>
      <h2 className="text-xl font-semibold text-vf-base">
        This interview is no longer available.
      </h2>
      <p className="text-sm text-vf-muted">
        Please reach out to the recruiter for next steps.
      </p>
    </CenterCard>
  );
}

export default function CandidateAcceptancePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const intent = (params.get("action") || "").toUpperCase();

  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  console.log(err);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showReject, setShowReject] = useState(intent === "REJECT");
  const [reason, setReason] = useState("");

  const fired = useRef(false);

  useEffect(() => {
    document.title = "Interview Confirmation — Ample Hire";
  }, []);

  useEffect(() => {
    if (fired.current || !token) return;
    fired.current = true;
    axios
      .get(`${SVC}/api/public/interview/respond/${token}`)
      .then((r) => setMeta(r.data))
      .catch((e) => {
        const code =
          e.response?.data?.error || `http_${e.response?.status || "error"}`;
        setErr(code);
      });
  }, [token]);

  async function submit(action) {
    setSubmitting(true);
    setErr(null);
    try {
      const r = await axios.post(`${SVC}/api/public/interview/respond`, {
        token,
        action,
        ...(action === "REJECT" && reason.trim()
          ? { reason: reason.trim() }
          : {}),
      });
      setResult(r.data);
    } catch (e) {
      const code =
        e.response?.data?.error || `http_${e.response?.status || "error"}`;
      setErr(code);
    } finally {
      setSubmitting(false);
    }
  }

  if (err) return <ErrorScreen code={err} />;
  if (!meta) return <LoadingScreen />;

  if (result?.status === "ACCEPTED") {
    return (
      <CenterCard>
        <div className="text-5xl">🎉</div>
        <h2 className="text-2xl font-semibold text-vf-base">
          You're confirmed!
        </h2>
        <p className="text-sm text-vf-muted">
          We've emailed you the interview join link at{" "}
          <b>{meta.candidateEmail}</b>.
        </p>
        <p className="text-sm text-vf-muted">
          You can close this tab — we'll see you on the call.
        </p>
      </CenterCard>
    );
  }

  if (result?.status === "REJECTED") {
    return (
      <CenterCard>
        <div className="text-5xl">✓</div>
        <h2 className="text-2xl font-semibold text-vf-base">
          Decision recorded.
        </h2>
        <p className="text-sm text-vf-muted">
          Thanks for letting us know. Reach out to the recruiter if you change
          your mind.
        </p>
      </CenterCard>
    );
  }

  if (meta.currentStatus !== "PENDING") {
    return <AlreadyDecidedScreen meta={meta} />;
  }

  return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-4">
      <div className="card max-w-xl w-full overflow-hidden p-0">
        <div className="bg-vf-base px-8 py-6">
          <img
            src="https://amplepayroll.com/wp-content/uploads/2024/06/ample_black-1.png"
            alt="Ample"
            className="h-8 brightness-0 invert"
          />
        </div>

        <div className="p-8 space-y-6">
          <div>
            <h1 className="text-xl font-semibold text-vf-base">
              Hi {meta.candidateName || "there"},
            </h1>
            <p className="text-sm text-vf-muted mt-2 leading-relaxed">
              You're invited to interview for{" "}
              <b className="text-vf-base">{meta.jobTitle || "this role"}</b>
              {meta.companyName ? (
                <>
                  {" "}
                  at <b className="text-vf-base">{meta.companyName}</b>
                </>
              ) : null}
              .
            </p>
          </div>

          <div className="bg-vf-tint rounded-xl p-5 space-y-3">
            {meta.interviewDate && (
              <div>
                <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-0.5">
                  Opens
                </div>
                <div className="text-sm font-medium text-vf-base">
                  {meta.interviewDate}
                </div>
              </div>
            )}
            {meta.interviewExpiry && (
              <div>
                <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-0.5">
                  Expires
                </div>
                <div className="text-sm font-medium text-vf-base">
                  {meta.interviewExpiry}
                </div>
              </div>
            )}
          </div>

          {!showReject ? (
            <div className="flex gap-3">
              <button
                onClick={() => submit("ACCEPT")}
                disabled={submitting}
                className="btn-primary flex-1 py-3 text-base disabled:opacity-50"
              >
                {submitting ? "Processing…" : "Confirm Accept"}
              </button>
              <button
                onClick={() => setShowReject(true)}
                disabled={submitting}
                className="btn-ghost flex-1 py-3 text-base disabled:opacity-50 text-vf-danger"
              >
                Decline
              </button>
            </div>
          ) : (
            <div className="bg-red-50 rounded-xl p-5 space-y-3">
              <div className="text-sm font-semibold text-red-700">
                Decline interview
              </div>
              <label className="block">
                <span className="text-xs text-red-600 mb-1 block">
                  Why are you declining? (optional)
                </span>
                <textarea
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-sm border border-red-200 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                  placeholder="e.g. Schedule conflict"
                />
                <div className="text-2xs text-red-500 mt-1 text-right">
                  {reason.length}/500
                </div>
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowReject(false)}
                  disabled={submitting}
                  className="btn-ghost btn-sm flex-1"
                >
                  Back
                </button>
                <button
                  onClick={() => submit("REJECT")}
                  disabled={submitting}
                  className="btn-danger btn-sm flex-1"
                >
                  {submitting ? "Submitting…" : "Confirm Decline"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className="px-8 py-4 text-center text-2xs text-vf-muted"
          style={{ borderTop: "0.5px solid rgba(99,102,241,0.15)" }}
        >
          This invitation was sent by Ample Hire on behalf of the hiring team.
        </div>
      </div>
    </div>
  );
}
